export const ASSOCIATIONS = ["CPRA", "PRCA", "FCA", "LRA", "BCRA", "CCA"];
export const SIDEBAR_ASSOCIATIONS = ["CPRA", "PRCA", "CCA", "FCA", "LRA"];

export const EVENT_CATEGORIES = [
  "Bareback Riding", "Steer Wrestling", "Team Roping (Header)", "Team Roping (Heeler)",
  "Saddle Bronc Riding", "Tie-Down Roping", "Barrel Racing", "Breakaway Roping", "Bull Riding",
];

export const SIDEBAR_EVENTS = [
  "Tie-Down Roping", "Team Roping (Header)", "Team Roping (Heeler)",
  "Steer Wrestling", "Barrel Racing",
];

export const money = (n) => "$" + Number(n).toLocaleString();

// Added money shown on a rodeo card, scoped to the user's selected events.
// No events selected -> full added money. Events selected -> only that event's money;
// if the rodeo has NO money for the selected event(s), amount is null so the card shows nothing.
export function addedMoneyForEvents(r, eventsSet) {
  const full = r.added_money || 0;
  if (!eventsSet || eventsSet.size === 0) return { amount: full, partial: false };
  const rset = new Set([...eventsSet].map(canonRodeoEvent));
  let sum = 0, matched = 0;
  for (const p of (r.purses || [])) {
    if (rset.has(canonRodeoEvent(p.event))) { sum += Number(p.amount) || 0; matched += 1; }
  }
  if (matched === 0) return { amount: null, partial: true };
  return { amount: sum, partial: true };
}

// Short abbreviations for the rodeo events, used in compact card lines.
const EVENT_ABBR = {
  "Bareback Riding": "BB", "Saddle Bronc Riding": "SB", "Bull Riding": "BR",
  "Tie-Down Roping": "TD", "Steer Wrestling": "SW", "Team Roping": "TR",
  "Team Roping (Header)": "TR-H", "Team Roping (Heeler)": "TR-L",
  "Barrel Racing": "BAR", "Breakaway Roping": "BKR", "Steer Roping": "SR",
  "Novice Bareback": "N-BB", "Novice Saddle Bronc": "N-SB", "Steer Riding": "STR",
};
export function eventAbbr(name) { return EVENT_ABBR[canonEvent(name)] || canonEvent(name); }

// Per-event purse breakdown for the rider's selected events (so the card can show
// "TD $2,050 · TR $1,025" instead of a single summed figure). Preserves purse order.
export function eventMoneyBreakdown(r, eventsSet) {
  if (!eventsSet || eventsSet.size === 0) return [];
  const rset = new Set([...eventsSet].map(canonRodeoEvent));
  const out = [];
  for (const p of (r.purses || [])) {
    const ce = canonEvent(p.event);
    if (rset.has(canonRodeoEvent(p.event))) out.push({ event: ce, abbr: eventAbbr(ce), amount: Number(p.amount) || 0 });
  }
  return out;
}

// Does this rodeo pass the rider's $-threshold filter? The threshold is PER DISCIPLINE:
// the rodeo qualifies if ANY of the rider's events pays at least `minMoney` in added money
// (so "TD $7,500 · TR $7,500" does NOT pass $15k+ — neither discipline alone hits $15k).
// If the rodeo has no per-event breakdown (total only), it can't be confirmed, so it fails.
// With no disciplines set, fall back to the rodeo total.
export function passesMoneyFilter(r, eventsSet, minMoney) {
  if (!minMoney || minMoney <= 0) return true;
  if (!eventsSet || eventsSet.size === 0) return (r.added_money || 0) >= minMoney;
  const br = eventMoneyBreakdown(r, eventsSet);
  if (!br.length) return false; // no per-event money for the rider's disciplines -> can't confirm
  return br.some((b) => (b.amount || 0) >= minMoney);
}

// Single source of truth for the money line shown on every rodeo card (Table/Calendar/Map).
// Disciplines known + the rodeo lists purses for them -> "TD $5,000 · TR $5,000".
// Otherwise -> the rodeo's total "$40,000 Added". Null only when there's truly no money.
export function moneyLabel(r, eventsSet) {
  const br = (eventsSet && eventsSet.size) ? eventMoneyBreakdown(r, eventsSet) : [];
  if (br.length) return br.map((b) => `${b.abbr} ${money(b.amount)}`).join(" · ");
  const total = r.added_money || 0;
  return total ? `${money(total)} Added` : null;
}

const KM_PER_MILE = 1.60934;

// A rodeo's "home" sanctioning body for filtering. Many Canadian rodeos are co-sanctioned
// CPRA+PRCA; a rider filtering PRCA wants US rodeos, not those — so Canadian sanctions win.
export function primaryAssoc(r) {
  const a = r?.associations || [];
  if (a.includes("CPRA")) return "CPRA";
  if (a.includes("CCA")) return "CCA";
  if (a.includes("PRCA")) return "PRCA";
  return a[0] || null;
}

// Dollars (added money, scoped to selected events) per mile from the rider's home base.
// Returns null when we can't compute it (no home set, rodeo lacking coords, or no money).
export function dollarsPerMile(r, home, eventsSet) {
  if (!home || !r || r.lat == null || r.lng == null) return null;
  const km = haversineKm(home, { lat: r.lat, lng: r.lng });
  const miles = km / KM_PER_MILE;
  if (!(miles >= 1)) return null; // home-town rodeos: distance too small to be meaningful
  const amt = addedMoneyForEvents(r, eventsSet).amount;
  if (!amt) return null;
  return amt / miles;
}

const EVENT_ALIASES = {
  "bareback": "Bareback Riding", "bareback riding": "Bareback Riding",
  "saddle bronc": "Saddle Bronc Riding", "saddle bronc riding": "Saddle Bronc Riding",
  "novice saddle bronc": "Novice Saddle Bronc", "novice saddlebronc": "Novice Saddle Bronc",
  "break away roping": "Breakaway Roping", "breakaway roping": "Breakaway Roping",
  "tie down roping": "Tie-Down Roping",
  "novice bareback": "Novice Bareback",
  "steer riding": "Steer Riding", "steer wrestling": "Steer Wrestling", "steer roping": "Steer Roping",
  "team roping": "Team Roping", "bull riding": "Bull Riding", "barrel racing": "Barrel Racing",
  "ladies barrel racing": "Barrel Racing", "cowgirls barrel racing": "Barrel Racing",
  "wpra barrel racing": "Barrel Racing", "girls barrel racing": "Barrel Racing",
  "ladies breakaway roping": "Breakaway Roping", "cowgirls breakaway roping": "Breakaway Roping",
  "breakaway": "Breakaway Roping", "header": "Team Roping (Header)", "heeler": "Team Roping (Heeler)",
  "team roping header": "Team Roping (Header)", "team roping heeler": "Team Roping (Heeler)",
  "team roping (header)": "Team Roping (Header)", "team roping (heeler)": "Team Roping (Heeler)",
  "tr header": "Team Roping (Header)", "tr heeler": "Team Roping (Heeler)",
  "tr hd": "Team Roping (Header)", "tr hl": "Team Roping (Heeler)",
};

// Normalise scraped event names (variant spellings/casing) to a single canonical label.
export function canonEvent(raw) {
  if (!raw) return "";
  const s = String(raw).replace(/^event[;:\s]+/i, "").trim();
  const key = s.replace(/[\s-]+/g, " ").trim().toLowerCase();
  return EVENT_ALIASES[key] || s;
}

// For MATCHING a rider's discipline against a rodeo's offered events / purses:
// team-roping header & heeler share the SAME team-roping entry/purse at a rodeo.
export function canonRodeoEvent(raw) {
  const e = canonEvent(raw);
  if (e === "Team Roping (Header)" || e === "Team Roping (Heeler)") return "Team Roping";
  return e;
}

export function ordinal(place) {
  if (place == null || place === "") return null;
  const s = String(place).trim();
  if (!/^\d+$/.test(s)) return s; // ties like "1-2" shown as-is
  const n = parseInt(s, 10);
  const suf = (n % 100 >= 11 && n % 100 <= 13) ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th";
  return `${n}${suf}`;
}

export function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// "Today" anchored to Mountain Time (America/Denver — MST/MDT, auto-handles DST),
// so entry-close countdowns roll over at Mountain midnight regardless of the
// viewer's device timezone. Returns 'YYYY-MM-DD'.
export function mountainTodayISO() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Denver" }).format(new Date());
}

// Whole-day difference between a date-only ISO and Mountain-time today.
export function daysUntilMountain(iso) {
  if (!iso) return null;
  const a = Date.parse(iso + "T00:00:00Z");
  const b = Date.parse(mountainTodayISO() + "T00:00:00Z");
  if (isNaN(a) || isNaN(b)) return null;
  return Math.round((a - b) / 86400000);
}

export function haversineKm(a, b) {
  const R = 6371, toR = (d) => (d * Math.PI) / 180;
  const dLat = toR(b.lat - a.lat), dLng = toR(b.lng - a.lng);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

// Reference point (Calgary, AB) for map distances
export const HOME_POINT = { lat: 51.0447, lng: -114.0719 };

// Deep-link target to ENTER a rodeo — the sanctioning body's entry/login site.
// CPRA (incl. CPRA+PRCA dual-sanctioned) is always taken on cpralogin.com, never the local rodeo site.
export function entryLink(r) {
  const a = r.associations || [];
  if (a.includes("CPRA")) return "https://cpralogin.com/login";
  if (a.includes("PRCA")) return "https://rodeoentry.prorodeo.org/";
  if (a.includes("CCA")) return "https://rodeocanada.com/";
  return r.website_url || r.draw_url || "https://cpralogin.com/login";
}
